import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Section, Eyebrow } from "@/components/PublicLayout";
import { ArrowRight } from "lucide-react";

const LEVELS = [
  ["all", "filter_all"],
  ["early_years", "filter_early_years"],
  ["school", "filter_k12"],
  ["foundation", "filter_foundation"],
  ["bachelor", "filter_bachelor"],
  ["master", "filter_master"],
  ["doctorate", "filter_doctorate"],
];

// The early-education ladder. English keeps two clear options (Pre-K, K–12);
// French/Spanish/German present the familiar four stages parents know.
const EARLY = "/programmes/early-years-parent-led";
const SCHOOL = "/schools/accelerated-studies";
const LADDER = {
  en: [
    { key: "prek", t: "Pre-K", age: "Ages 2–5 · Parent-led", d: "Learning begins at home. After a Simo seminar, parents lead short, joyful daily sessions that seed language, curiosity and number sense.", href: EARLY },
    { key: "k12", t: "K–12", age: "Ages 5–18 · Mastery-paced", d: "Primary through secondary as one accelerated path — deep conceptual mathematics, research from childhood and early qualification, often years ahead of age.", href: SCHOOL },
    { key: "he", t: "Higher Education", age: "16+ · Bachelor, Master, Doctorate", d: "Our university divisions — Simo Business School (SBS) and the Simo Institute of Technology (SIT) — deliver online-first degrees built on the same mastery principle.", href: "#browse-programmes" },
  ],
  fr: [
    { key: "creche", t: "Crèche", age: "2–3 ans · Éveil guidé par les parents", d: "L'apprentissage commence à la maison. Après un séminaire Simo, les parents mènent de courtes séances d'éveil, de langage et de jeu.", href: EARLY },
    { key: "maternelle", t: "Maternelle", age: "3–5 ans · Langage & curiosité", d: "Langage riche, sens du nombre et habitude d'expliquer, semés tôt en deux ou trois langues — toujours à la maison, avec vous.", href: EARLY },
    { key: "primaire", t: "Primaire", age: "6–11 ans · Au rythme de la maîtrise", d: "Mathématiques conceptuelles approfondies, recherche dès l'enfance et explication orale enregistrée.", href: SCHOOL },
    { key: "secondaire", t: "Secondaire", age: "11–16 ans · Accéléré", d: "Raisonnement avancé et préparation précoce aux examens, souvent des années en avance sur l'âge.", href: SCHOOL },
    { key: "superieur", t: "Enseignement supérieur", age: "16+ · Licence, Master, Doctorat", d: "Nos divisions universitaires — Simo Business School (SBS) et Simo Institute of Technology (SIT) — proposent des diplômes en ligne fondés sur le même principe de maîtrise.", href: "#browse-programmes" },
  ],
  es: [
    { key: "guarderia", t: "Guardería", age: "2–3 años · Guiado por la familia", d: "El aprendizaje empieza en casa. Tras un seminario Simo, las familias dirigen sesiones diarias breves de estímulo, lenguaje y juego.", href: EARLY },
    { key: "preescolar", t: "Preescolar", age: "3–5 años · Lenguaje y curiosidad", d: "Lenguaje rico, sentido numérico y el hábito de explicar, sembrados pronto en dos o tres idiomas — siempre en casa, contigo.", href: EARLY },
    { key: "primaria", t: "Primaria", age: "6–11 años · Al ritmo del dominio", d: "Matemáticas conceptuales profundas, investigación desde la niñez y explicación oral grabada.", href: SCHOOL },
    { key: "secundaria", t: "Secundaria", age: "11–16 años · Acelerado", d: "Razonamiento avanzado y preparación temprana de titulaciones, a menudo años por delante de la edad.", href: SCHOOL },
    { key: "superior", t: "Educación superior", age: "16+ · Grado, Máster, Doctorado", d: "Nuestras divisiones universitarias — Simo Business School (SBS) y Simo Institute of Technology (SIT) — ofrecen títulos online basados en el mismo principio de dominio.", href: "#browse-programmes" },
  ],
  de: [
    { key: "krippe", t: "Krippe", age: "2–3 Jahre · Elterngeführt", d: "Lernen beginnt zu Hause. Nach einem Simo-Seminar leiten Eltern kurze, freudvolle tägliche Einheiten für Sprache, Neugier und Spiel.", href: EARLY },
    { key: "kindergarten", t: "Kindergarten", age: "3–5 Jahre · Sprache & Neugier", d: "Reiche Sprache, Zahlensinn und die Gewohnheit des Erklärens, früh in zwei oder drei Sprachen angelegt — immer zu Hause, mit Ihnen.", href: EARLY },
    { key: "grundschule", t: "Grundschule", age: "6–11 Jahre · Im Tempo der Meisterschaft", d: "Tiefes konzeptuelles Rechnen, Forschung ab der Kindheit und aufgezeichnete mündliche Erklärung.", href: SCHOOL },
    { key: "sekundar", t: "Sekundarstufe", age: "11–16 Jahre · Beschleunigt", d: "Fortgeschrittenes Denken und frühe Prüfungsvorbereitung, oft Jahre voraus.", href: SCHOOL },
    { key: "hochschule", t: "Hochschulbildung", age: "16+ · Bachelor, Master, Doktorat", d: "Unsere Hochschulbereiche — Simo Business School (SBS) und Simo Institute of Technology (SIT) — bieten Online-Abschlüsse nach demselben Meisterschaftsprinzip.", href: "#browse-programmes" },
  ],
};

export default function Programmes() {
  const { t, field, lang } = useI18n();
  const [params] = useSearchParams();
  const [progs, setProgs] = useState([]);
  const [level, setLevel] = useState(params.get("level") || "all");
  const school = params.get("school") || "";
  useEffect(() => { api.get("/programmes").then((r) => setProgs(r.data)); }, []);
  useEffect(() => { if (params.get("level")) setLevel(params.get("level")); }, [params]);
  let filtered = level === "all" ? progs : progs.filter((p) => p.level === level);
  if (school) filtered = filtered.filter((p) => p.school === school);

  const ladder = LADDER[lang] || LADDER.en;
  const ladderCols = ladder.length <= 2 ? "md:grid-cols-2" : ladder.length === 3 ? "md:grid-cols-3" : ladder.length >= 5 ? "md:grid-cols-3 lg:grid-cols-5" : "md:grid-cols-2 lg:grid-cols-4";

  return (
    <Section className="py-16 md:py-24">
      <Eyebrow>{t("prog_eyebrow")}</Eyebrow>
      <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-ink" data-testid="programmes-title">{t("prog_title")}</h1>
      <p className="mt-5 max-w-2xl text-ink/65 leading-relaxed">{t("prog_intro")}</p>

      {/* The mastery ladder — early education, clearly named per language */}
      <div className="mt-14">
        <Eyebrow>{t("prog_ladder_eyebrow")}</Eyebrow>
        <p className="max-w-2xl text-ink/60 leading-relaxed">{t("prog_ladder_intro")}</p>
        <div className={`mt-8 grid gap-px bg-border border border-border ${ladderCols}`} data-testid="mastery-ladder">
          {ladder.map((s, i) => {
            const Card = ({ children }) => s.href.startsWith("#")
              ? <a href={s.href} data-testid={`stage-${s.key}`} className="group bg-paper p-7 hover:bg-secondary transition-colors flex flex-col">{children}</a>
              : <Link to={s.href} data-testid={`stage-${s.key}`} className="group bg-paper p-7 hover:bg-secondary transition-colors flex flex-col">{children}</Link>;
            return (
              <Card key={s.key}>
                <div className="font-mono text-xs text-heraldic">0{i + 1}</div>
                <div className="mt-2 font-serif text-2xl text-ink">{s.t}</div>
                <div className="mt-1 text-xs uppercase tracking-wide text-ink/45">{s.age}</div>
                <p className="mt-3 text-sm text-ink/60 leading-relaxed flex-1">{s.d}</p>
                <ArrowRight className="mt-4 h-4 w-4 text-ink/30 group-hover:translate-x-1 group-hover:text-heraldic transition-all" />
              </Card>
            );
          })}
        </div>
      </div>

      {/* Browse all programmes */}
      <div className="mt-16" id="browse-programmes">
        <Eyebrow>{t("prog_browse_eyebrow")}</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {LEVELS.map(([v, l]) => (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setLevel(v)}
              className={`px-4 py-1.5 text-sm rounded-sm border transition-colors ${level === v ? "bg-ink text-paper border-ink" : "border-border text-ink/70 hover:border-ink"}`}>{t(l)}</button>
          ))}
        </div>
        <div className="mt-8 grid gap-px bg-border border border-border md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <Link key={p.slug} to={`/programmes/${p.slug}`} data-testid={`prog-card-${p.slug}`} className="group bg-paper p-7 hover:bg-secondary transition-colors flex flex-col">
              <div className="font-mono text-xs text-heraldic uppercase">{p.level} · {p.duration}</div>
              <div className="mt-2 font-serif text-xl text-ink leading-snug flex-1">{field(p, "name")}</div>
              <div className="mt-3 text-sm text-ink/55 line-clamp-3">{field(p, "purpose")}</div>
              <ArrowRight className="mt-4 h-4 w-4 text-ink/30 group-hover:translate-x-1 group-hover:text-heraldic transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </Section>
  );
}
